import { useReducer } from 'react';

const documentTypes = {
    company_accounts: 'company_accounts',
    bank_statement: 'bank_statement',
    business_accounts: 'business_accounts',
    vat_return: 'vat_return',
    invoices: 'invoices',
    personal_id: 'personal_id',
    customer_broker_consent: 'customer_broker_consent',
    balance_sheet: 'balance_sheet',
    profit_and_loss: 'profit_and_loss',
    company_bylaws: 'company_bylaws',
    nif_card: 'nif_card',
    modelo: 'modelo',
    management_accounts: 'management_accounts',
    management_and_balance_accounts: 'management_and_balance_accounts',
    year_end_management_and_balance_accounts:
        'year_end_management_and_balance_accounts',
    aged_debtor_report: 'aged_debtor_report',
    business_plan: 'business_plan',
    self_assessment_tax_return: 'self_assessment_tax_return',
    personal_bank_statement: 'personal_bank_statement',
    other: 'other',
    cash_register: 'cash_register',
    cashflow_forecast: 'cashflow_forecast',
    outstanding_debt_information: 'outstanding_debt_information',
    list_of_shareholders: 'list_of_shareholders',
    cbils_questionnaire: 'cbils_questionnaire'
};

const deDocumentType = {
    bank_statement: `Bank Statement`,
    management_accounts: `Management Accounts`,
    management_and_balance_accounts: `Management and Balance Accounts`,
    year_end_management_accounts: `Year End Management Accounts`,
    year_end_management_and_balance_accounts: `Year End Management And Balance Accounts`,
    share_registry: `Share Registry`,
    articles_of_association: `Articles of Association`,
    trade_certificate: `Trade Certificate`,
    other: `Other`,
    company_accounts: `Company Accounts`
};

type TDocumentType = keyof typeof documentTypes;
type TDocumentTypeDE = keyof typeof deDocumentType;

type TDocumentStatus = 'complete' | 'rejected' | 'pending';

type TDocuments = {
    attributes: {
        document_name: string;
        document_type: string;
        end_date: Date;
        reject_reason: TUploadRejectReasonCode;
        start_date: Date;
        status: TDocumentStatus;
        uploaded_at: Date;
    };
    id: string;
    relationships: {
        bank_account: {
            data: {
                id: string;
                type: string;
            };
        };
    };
    type: TDocumentType & TDocumentTypeDE;
    rejected?: number;
};

export type TFileStatus = 'loading' | 'error' | 'success' | 'rejected';

type TFileStatusGroup = 'uploaded' | 'approved' | 'rejected';

export type TUploadRejectReasonCode =
    | 'ILLEGIBLE'
    | 'PASSWORD_PROTECTED'
    | 'MISSING_TRANSACTIONS'
    | 'MISSING_ACCOUNT_DETAILS'
    | 'MISSING_BALANCES'
    | 'INCOMPLETE_REFERENCES'
    | 'OTHER'
    | 'DUPLICATE'
    | 'UNSUPPORTED_EXTENSION'
    | 'UNSUPPORTED_FORMAT'
    | 'NO_RELEVANT_DATA'
    | 'OUTDATED_INFORMATION';

const translateUploadRejectReason = (reasonCode: TUploadRejectReasonCode) => {
    switch (reasonCode) {
        case 'ILLEGIBLE':
            return 'Illegible';
        case 'PASSWORD_PROTECTED':
            return 'Password not available';
        case 'MISSING_TRANSACTIONS':
            return 'Missing transactions';
        case 'MISSING_ACCOUNT_DETAILS':
            return 'Missing account details';
        case 'MISSING_BALANCES':
            return 'Missing balances';
        case 'INCOMPLETE_REFERENCES':
            return 'References missing';
        case 'OTHER':
            return 'Other';
        case 'DUPLICATE':
            return 'Duplicate data';
        case 'UNSUPPORTED_EXTENSION':
            return 'Invalid extension';
        case 'UNSUPPORTED_FORMAT':
            return 'Invalid document format';
        case 'NO_RELEVANT_DATA':
            return 'Relevant data not found';
        case 'OUTDATED_INFORMATION':
            return 'Outdated information';
    }
};

enum ACTIONS {
    SET_ALL_FILES = 'SET_ALL_FILES',
    SET_FILE_LOADING = 'SET_FILE_LOADING',
    SET_FILE_SUCCESS = 'SET_FILE_SUCCESS',
    SET_FILE_ERROR = 'SET_FILE_ERROR',
    SET_FILE_REJECT = 'SET_FILE_REJECT',
    REMOVE_FILE = 'REMOVE_FILE'
}

export type TFile = {
    id: string;
    fileName: string;
    status: TFileStatus;
    statusGroup: TFileStatusGroup;
    errorReason?: string;
    rejectReason?: string;
};

type TSetAllFilesAction = {
    type: typeof ACTIONS.SET_ALL_FILES;
    payload: {
        files: {
            id: string;
            attributes: {
                document_name: string;
                status: string;
                reject_reason?: TUploadRejectReasonCode;
            };
        }[];
    };
};

type TSetRemoveAction = {
    type: ACTIONS.REMOVE_FILE;
    payload: {
        id: string;
    };
};

type TSetLoadingAction = {
    type: ACTIONS.SET_FILE_LOADING;
    payload: {
        id: string;
        fileName: string;
    };
};

type TSetSuccessAction = {
    type: ACTIONS.SET_FILE_SUCCESS;
    payload: {
        id: string;
        status: TFileStatus;
    };
};

type TSetErrorAction = {
    type: ACTIONS.SET_FILE_ERROR;
    payload: {
        id: string;
        errorReason: string;
    };
};

type TSetRejectAction = {
    type: ACTIONS.SET_FILE_REJECT;
    payload: {
        id: string;
        rejectReason: TUploadRejectReasonCode;
    };
};

type TReducerAction =
    | TSetAllFilesAction
    | TSetRemoveAction
    | TSetLoadingAction
    | TSetSuccessAction
    | TSetErrorAction
    | TSetRejectAction;

export function useFileUploadReducer() {
    const reducer = (state: TFile[], action: TReducerAction) => {
        switch (action.type) {
            case ACTIONS.SET_ALL_FILES:
                return [
                    ...state,
                    ...action.payload.files?.map((file) => {
                        const isRejected =
                            file.attributes.status === 'rejected';

                        const fileData: TFile = {
                            id: file.id,
                            fileName: file.attributes.document_name,
                            status: isRejected
                                ? ('rejected' as TFileStatus)
                                : ('success' as TFileStatus),
                            statusGroup: isRejected
                                ? ('rejected' as TFileStatusGroup)
                                : file.attributes.status === 'complete'
                                  ? ('approved' as TFileStatusGroup)
                                  : ('uploaded' as TFileStatusGroup)
                        };

                        if (isRejected && !file.attributes.reject_reason) {
                            fileData.rejectReason =
                                'Document cannot be validated. Please upload another document.';
                        }

                        if (isRejected && file.attributes.reject_reason) {
                            fileData.rejectReason = translateUploadRejectReason(
                                file.attributes.reject_reason
                            );
                        }
                        return fileData;
                    })
                ];
            case ACTIONS.SET_FILE_LOADING:
                return [
                    ...state,
                    {
                        id: action.payload.id,
                        fileName: action.payload.fileName,
                        status: 'loading' as TFileStatus,
                        statusGroup: 'uploaded' as TFileStatusGroup
                    }
                ];
            case ACTIONS.SET_FILE_ERROR: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );
                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: 'error' as TFileStatus,
                    errorReason: action.payload.errorReason
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }
            case ACTIONS.SET_FILE_REJECT: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );

                const rejectReason = action.payload.rejectReason
                    ? translateUploadRejectReason(action.payload.rejectReason)
                    : 'Document cannot be validated. Please upload another document.';

                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: 'rejected' as TFileStatus,
                    rejectReason: rejectReason,
                    statusGroup: 'rejected' as TFileStatusGroup
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }
            case ACTIONS.SET_FILE_SUCCESS: {
                const fileToUpdateIndex = state.findIndex(
                    (file) => file.id === action.payload.id
                );
                const updatedFile = {
                    ...state[fileToUpdateIndex],
                    status: action.payload.status
                };

                return state
                    .slice(0, fileToUpdateIndex)
                    .concat(updatedFile)
                    .concat(state.slice(fileToUpdateIndex + 1));
            }
            case ACTIONS.REMOVE_FILE:
                return state.filter((file) => file.id !== action.payload.id);
            default:
                return state;
        }
    };

    const [uploadedFiles, dispatch] = useReducer(reducer, []);

    const setAllFiles = (files: TDocuments[]) => {
        dispatch({
            type: ACTIONS.SET_ALL_FILES,
            payload: {
                files
            }
        });
    };

    const setFileLoading = (id: string, fileName: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_LOADING,
            payload: {
                id,
                fileName
            }
        });
    };

    const setFileSuccess = (id: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_SUCCESS,
            payload: {
                id,
                status: 'success'
            }
        });
    };

    const setFileError = (id: string, errorReason: string) => {
        dispatch({
            type: ACTIONS.SET_FILE_ERROR,
            payload: {
                id,
                errorReason
            }
        });
    };

    const setFileReject = (
        id: string,
        rejectReason: TUploadRejectReasonCode
    ) => {
        dispatch({
            type: ACTIONS.SET_FILE_REJECT,
            payload: {
                id,
                rejectReason
            }
        });
    };

    const removeFile = (id: string) => {
        dispatch({
            type: ACTIONS.REMOVE_FILE,
            payload: {
                id
            }
        });
    };

    return {
        uploadedFiles,
        setAllFiles,
        setFileLoading,
        setFileSuccess,
        setFileError,
        setFileReject,
        removeFile
    };
}
